import { Suspense, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/ui/theme-provider";
import ConsentProvider from "@/components/consent/ConsentProvider";
import AuthProvider from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import AppDownloadBanner from "@/components/common/AppDownloadBanner";
// MAR-238: eager, because a redirect in its own lazy chunk shows the Suspense
// fallback before it can redirect.
import MumHubMembershipRedirect from "@/pages/MumHubMembershipRedirect";
import { OutsideUKNotice } from "@/components/common/OutsideUKNotice";

// When a new deploy ships, the old index.html in an open tab still references
// hashed chunks that no longer exist on Cloudflare Pages. Detect that failure
// mode and force one reload to pick up the new HTML + chunks. The one-shot
// session guard that stops that turning into a reload loop lives in
// @/lib/chunk-reload, which AppErrorBoundary shares — read the comments there
// before changing any of this.
import { lazyWithReload } from "@/lib/lazy-with-reload";
import { useMumHubGate } from "@/lib/mum-hub/gate";

// Vite preloads dependencies of lazy chunks via its internal __vitePreload
// helper, outside of React.lazy's factory. When those preloads fail after a
// deploy (stale hashes), Vite emits `vite:preloadError` on window. Swallow it
// so it doesn't reach the global unhandledrejection handler.
//
// This deliberately does NOT reload. A preload failure is a *background*
// event — nothing the customer can see is broken yet — and reloading on it
// costs more than it buys:
//
//   - Budget theft: the self-heal reload budget is small and shared, so a
//     failed background preload spends the reload that the subsequent real
//     navigation failure needs, and that one lands on the error screen.
//   - Data loss: it reloads a page that hasn't crashed, discarding whatever
//     the customer had typed. Neither Contact nor the quiz pages persist
//     in-progress input. The error-boundary path doesn't have this problem —
//     by the time componentDidCatch runs, that tree is already gone.
//
// The reactive path covers the same failure a beat later: if the preload was
// for a chunk the customer actually navigates to, lazyWithReload sees the
// import reject and self-heals then, when a reload is free.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
  });
}

const Toaster = lazyWithReload(() =>
  // Optional chaining: after a deploy, a stale modulepreload can make import()
  // resolve to an undefined module. `m.Toaster` would then throw a TypeError
  // inside this mapper before lazyWithReload's missing-default guard runs.
  // `m?.Toaster` yields `{ default: undefined }` so that guard handles it.
  import("@/components/ui/toaster").then((m) => ({ default: m?.Toaster }))
);

// Home is eager-imported so there is no loading flicker on the first paint
// of the landing page; every other route is lazy-loaded so its dependencies
// (react-hook-form, zod, Radix primitives, etc.) are not pulled into the
// main bundle.
import Home from "@/pages/Home";
const Shop = lazyWithReload(() => import("@/pages/Shop"));
const About = lazyWithReload(() => import("@/pages/About"));
const Contact = lazyWithReload(() => import("@/pages/Contact"));
const HowIChoose = lazyWithReload(() => import("@/pages/HowIChoose"));
const Privacy = lazyWithReload(() => import("@/pages/Privacy"));
const Terms = lazyWithReload(() => import("@/pages/Terms"));
const StyleHubIndex = lazyWithReload(() => import("@/pages/StyleHubIndex"));
const StyleHubArticle = lazyWithReload(() => import("@/pages/StyleHubArticle"));
const NotFound = lazyWithReload(() => import("@/pages/not-found"));
const Account = lazyWithReload(() => import("@/pages/Account"));
const AccountLogin = lazyWithReload(() => import("@/pages/AccountLogin"));
const MyHub = lazyWithReload(() => import("@/pages/MyHub"));
const SavedPieces = lazyWithReload(() => import("@/pages/SavedPieces"));
const BodyTypeQuiz = lazyWithReload(() => import("@/pages/BodyTypeQuiz"));
const ColourPaletteQuiz = lazyWithReload(() => import("@/pages/ColourPaletteQuiz"));
const StyleQuizLanding = lazyWithReload(() => import("@/pages/StyleQuizLanding"));
const PublicBodyShapeQuiz = lazyWithReload(() => import("@/pages/PublicBodyShapeQuiz"));
const PublicColourAnalysisQuiz = lazyWithReload(() => import("@/pages/PublicColourAnalysisQuiz"));
// MAR-219. Lazy like every other non-home route, so that with the flag off the
// three chunks are built but never requested by anybody, and with it on they
// cost the customer nothing until she goes there.
const MumHub = lazyWithReload(() => import("@/pages/MumHub"));
const MumHubSetup = lazyWithReload(() => import("@/pages/MumHubSetup"));
const MumHubStage = lazyWithReload(() => import("@/pages/MumHubStage"));

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

/**
 * MAR-219: everything under `/mum-hub`, behind the gate.
 *
 * The three states are three different renders and each matters:
 *
 * - PENDING. An empty page of the right colour, identical to the Suspense
 *   fallback above, so a customer whose entitlement is still being read sees a
 *   blank beat rather than a not-found page that then vanishes.
 * - NOT VISIBLE. The real not-found page, which is what someone who guessed
 *   the URL should get for a feature that is not open to them.
 * - VISIBLE. The three routes.
 *
 * The nested Switch, rather than three sibling routes in the outer one, is
 * what lets the gate be consulted once for all of them.
 */
function MumHubRoutes() {
  const gate = useMumHubGate();
  if (gate.pending) return <div className="min-h-screen bg-warm-white" />;
  if (!gate.visible) return <NotFound />;
  return (
    <Switch>
      <Route path="/mum-hub" component={MumHub} />
      <Route path="/mum-hub/setup" component={MumHubSetup} />
      <Route path="/mum-hub/stage" component={MumHubStage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function Router() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-warm-white" />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/shop" component={Shop} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />
        <Route path="/how-i-choose" component={HowIChoose} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms-and-conditions" component={Terms} />
        <Route path="/style-hub" component={StyleHubIndex} />
        <Route path="/style-hub/:slug" component={StyleHubArticle} />
        <Route path="/account" component={Account} />
        <Route path="/account/login" component={AccountLogin} />
        <Route path="/account/membership" component={MumHubMembershipRedirect} />
        <Route path="/my-hub" component={MyHub} />
        <Route path="/my-hub/saved" component={SavedPieces} />
        <Route path="/quiz" component={StyleQuizLanding} />
        <Route path="/quiz/body-type" component={BodyTypeQuiz} />
        <Route path="/quiz/colour-palette" component={ColourPaletteQuiz} />
        <Route path="/body-shape-quiz" component={PublicBodyShapeQuiz} />
        <Route path="/colour-analysis-quiz" component={PublicColourAnalysisQuiz} />
        {/* MAR-219: Mum Hub. Registered unconditionally and gated INSIDE,
            because the gate now has a third answer. The build flag and the
            browser override are known synchronously, but an entitled customer
            is not: that answer comes from the Worker. Leaving these routes
            unregistered until it arrives would render the not-found page and
            then replace it, which is a flash of 404 on a page she has paid
            for. `MumHubRoutes` draws nothing while it is waiting. */}
        <Route path="/mum-hub" component={MumHubRoutes} />
        <Route path="/mum-hub/:rest*" component={MumHubRoutes} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ConsentProvider>
          <AuthProvider>
            <div className="min-h-screen bg-warm-white dark:bg-gray-900 text-charcoal dark:text-gray-100 transition-colors duration-300">
              <ScrollToTop />
              <Header />
              <main>
                <Router />
              </main>
              <Footer />
            </div>
            {/* MAR-130. Client-only, so it never appears in prerendered HTML.
                Fixed to the bottom of the viewport, except on Style Hub
                articles, where it is an in-flow row and must stay the last
                thing in the document so it cannot cover or push anything.
                Mounted as a sibling of the page wrapper for that reason. */}
            <AppDownloadBanner />
            {/* Client-only and outside document flow, for the same reasons as
                the banner above: it must never reach the prerendered HTML, and
                the time zone it keys on is a browser answer. */}
            <OutsideUKNotice />
            <Suspense fallback={null}>
              <Toaster />
            </Suspense>
          </AuthProvider>
        </ConsentProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
